import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { brand, legal, navLinks } from "../content/site";
import { Logo } from "./Logo";
import { MaskLine } from "./Reveal";

const legalLinks = [
  { label: "Shipping & Returns", to: "/shipping-returns" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms" },
];

export function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#1A1A1A] text-stone-200">
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-20 sm:px-8 lg:px-16 lg:pt-28">
        <div className="border-b border-stone-100/10 pb-16">
          <MaskLine inView>
            <p className="font-serif text-4xl font-light leading-tight text-stone-50 sm:text-5xl lg:text-6xl">
              Wellness, Simply.
              <img
                src="/assets/favicon.png"
                alt=""
                aria-hidden="true"
                className="ml-0.5 inline-block h-[0.5em] w-auto -translate-y-[0.05em]"
              />
            </p>
          </MaskLine>
          <Link
            to="/shop"
            data-testid="footer-shop-cta"
            className="mt-8 inline-flex items-center gap-2 border border-stone-100/30 px-7 py-3.5 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-stone-50 transition-colors duration-300 hover:bg-stone-50 hover:text-stone-900"
          >
            Shop Nº 01 <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>

        <div className="grid gap-12 py-14 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo dark />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-stone-300">
              Premium UK food supplements, thoughtfully formulated with carefully selected ingredients, complete
              transparency and uncompromising quality.
            </p>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400">Explore</p>
            <ul className="mt-5 space-y-3 text-sm">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-stone-200 transition-colors duration-300 hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400">Legal</p>
            <ul className="mt-5 space-y-3 text-sm">
              {legalLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-stone-200 transition-colors duration-300 hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400">Contact</p>
            <a
              href={`mailto:${brand.email}`}
              data-testid="footer-email-link"
              className="mt-5 block text-sm text-stone-200 underline-offset-4 transition-colors duration-300 hover:text-white hover:underline"
            >
              {brand.email}
            </a>
            <p className="mt-3 text-sm text-stone-300">{brand.domain}</p>
          </div>
        </div>

        <div className="border-t border-stone-100/10 pt-10">
          <div data-testid="footer-signature">
            <p className="font-serif text-xl font-light italic text-stone-100">
              Wellness, Simply.
              <img
                src="/assets/favicon.png"
                alt=""
                aria-hidden="true"
                className="ml-0.5 inline-block h-[0.75em] w-auto -translate-y-[0.05em]"
              />
            </p>
          </div>
          <p className="mt-7 text-xs leading-relaxed text-[#D9D9D9]">
            Food supplements should not be used as a substitute for a varied, balanced diet and a healthy lifestyle.
            EL Well products are not intended to diagnose, treat, cure or prevent any disease.
          </p>
          <div className="mt-6 space-y-1 text-xs leading-relaxed text-[#D9D9D9]">
            <p>{legal.company}</p>
            <p>Registered in England and Wales</p>
            <p>Company No: {legal.companyNumber}</p>
            <p>Registered Office: {legal.registeredOffice}</p>
          </div>
          <p className="mt-4 text-xs text-[#D9D9D9]">
            © {new Date().getFullYear()} {brand.tm} · {legal.company} · All rights reserved.
          </p>
          <p
            data-testid="footer-motto"
            className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500"
          >
            Founded with purpose. Built on quality.
          </p>
        </div>
      </div>
    </footer>
  );
}
